import * as Dialog from "@radix-ui/react-dialog";
import type { CSSProperties, ReactNode } from "react";
import React from "react";

import "./kit.css";

export type T_SheetProps = {
  children: ReactNode;
  closeOnBackdropPress?: boolean;
  onClose: () => void;
  panelClassName?: string;
  panelStyle?: CSSProperties;
  /** Accessible dialog title (visually hidden; sheets carry their own headers). */
  title: string;
  visible: boolean;
};

/**
 * Canonical app sheet — web mirror of the mobile Sheet. Radix Dialog supplies
 * the mechanics (portal, focus trap + restore, Escape, backdrop dismissal);
 * presentation is a bottom sheet with grabber at narrow viewports and a
 * centered fade/scale modal at desktop widths (recorded divergence: mobile is
 * always bottom-anchored with a blur backdrop; web uses a scrim without blur).
 */
export function Sheet({
  children,
  closeOnBackdropPress = true,
  onClose,
  panelClassName,
  panelStyle,
  title,
  visible,
}: T_SheetProps) {
  return (
    <Dialog.Root
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      open={visible}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="lk-sheet__overlay" />
        <Dialog.Content
          aria-describedby={undefined}
          className={
            panelClassName
              ? `lk-sheet__panel ${panelClassName}`
              : "lk-sheet__panel"
          }
          onInteractOutside={(event) => {
            if (!closeOnBackdropPress) event.preventDefault();
          }}
          style={panelStyle}
        >
          <Dialog.Title className="lk-visually-hidden">{title}</Dialog.Title>
          <div aria-hidden className="lk-sheet__grabber" />
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
